import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, RefreshCw, Rocket, ArrowUpRight, ArrowDownRight, X, Save, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency as fmtCur } from '@/lib/market';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_CONFIG = {
  pre_seed:     { label: 'Pre-Seed'  },
  seed:         { label: 'Seed'      },
  series_a:     { label: 'Series A'  },
  series_b:     { label: 'Series B'  },
  series_c:     { label: 'Series C'  },
  series_d_plus:{ label: 'Series D+' },
  growth:       { label: 'Growth'    },
  pre_ipo:      { label: 'Pre-IPO'   },
  public:       { label: 'Public'    },
};

const STATUS_CONFIG = {
  active:    { label: 'Active',    dot: 'bg-emerald-500' },
  acquired:  { label: 'Acquired',  dot: 'bg-[#ffd87a]'  },
  ipo:       { label: 'IPO',       dot: 'bg-[#ffd87a]'  },
  shut_down: { label: 'Shut Down', dot: 'bg-[#c97a3a]'  },
  zombie:    { label: 'Zombie',    dot: 'bg-[#a8a8a8]'  },
};

function StatCard({ label, value, sub }) {
  return (
    <div className="p-4 bg-white border border-[#e5e5e5] rounded-lg shadow-sm h-full">
      <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">{label}</p>
      <p className="text-lg font-bold text-[#1a1a1a]">{value}</p>
      {sub && <p className="text-xs text-[#4a4a4a] mt-0.5">{sub}</p>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#4a4a4a] uppercase tracking-wider mb-1">{label}</label>
      {children}
    </div>
  );
}

function NumInput({ value, onChange, placeholder }) {
  return (
    <input
      type="number"
      value={value ?? ''}
      onChange={e => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
      placeholder={placeholder || '—'}
      className="w-full px-3 py-2 text-sm border border-[#e5e5e5] focus:border-[#1a1a1a] outline-none bg-white text-[#1a1a1a]"
    />
  );
}

function TextInput({ value, onChange, placeholder }) {
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={e => onChange(e.target.value || null)}
      placeholder={placeholder || '—'}
      className="w-full px-3 py-2 text-sm border border-[#e5e5e5] focus:border-[#1a1a1a] outline-none bg-white text-[#1a1a1a]"
    />
  );
}

function EditDrawer({ company, onClose, onSaved, fmt }) {
  const [investments, setInvestments] = useState([]);
  const [loadingInv, setLoadingInv] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/startups/investments?startup_id=${company.id}`)
      .then(r => {
        const invs = r.data || [];
        setInvestments(invs);
        const d = {};
        invs.forEach(inv => {
          d[inv.id] = {
            round_name: inv.round_name,
            investment_date: inv.investment_date,
            investment_amount: inv.investment_amount,
            entry_valuation: inv.entry_valuation ?? null,
            ownership_percentage: inv.ownership_percentage ?? null,
            current_value: inv.current_value ?? null,
            notes: inv.notes ?? null,
          };
        });
        setDrafts(d);
      })
      .catch(() => toast.error('Failed to load investments'))
      .finally(() => setLoadingInv(false));
  }, [company.id]);

  const setField = (invId, field, value) =>
    setDrafts(prev => ({ ...prev, [invId]: { ...prev[invId], [field]: value } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all(
        investments.map(inv =>
          axios.put(`${API}/startups/investments/${inv.id}`, drafts[inv.id])
        )
      );
      toast.success('Saved successfully');
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e5e5e5] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#1a1a1a] flex items-center justify-center">
              <span className="text-[10px] font-bold text-[#ffd87a]">{company.name?.slice(0, 2).toUpperCase()}</span>
            </div>
            <div>
              <p className="font-semibold text-[#1a1a1a]">{company.name}</p>
              <p className="text-xs text-[#4a4a4a]">{STAGE_CONFIG[company.stage]?.label || company.stage} · {company.sector || '—'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#4a4a4a] hover:text-[#1a1a1a]"><X className="w-5 h-5" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {loadingInv ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="w-5 h-5 animate-spin text-[#ffd87a]" />
            </div>
          ) : investments.length === 0 ? (
            <p className="text-sm text-[#4a4a4a] text-center py-8">No investment records found.</p>
          ) : (
            investments.map((inv, idx) => {
              const d = drafts[inv.id] || {};
              return (
                <div key={inv.id} className="border border-[#e5e5e5] p-4 space-y-4">
                  <p className="text-xs font-semibold text-[#4a4a4a] uppercase tracking-wider">
                    Investment {investments.length > 1 ? idx + 1 : ''} · {inv.investment_type?.replace('_', ' ')}
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Round">
                      <TextInput value={d.round_name} onChange={v => setField(inv.id, 'round_name', v)} />
                    </Field>
                    <Field label="Date">
                      <input
                        type="date"
                        value={d.investment_date?.slice(0, 10) ?? ''}
                        onChange={e => setField(inv.id, 'investment_date', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-[#e5e5e5] focus:border-[#1a1a1a] outline-none bg-white text-[#1a1a1a]"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Amount Invested">
                      <NumInput value={d.investment_amount} onChange={v => setField(inv.id, 'investment_amount', v)} />
                    </Field>
                    <Field label="Entry Valuation">
                      <NumInput value={d.entry_valuation} onChange={v => setField(inv.id, 'entry_valuation', v)} placeholder="Company valuation at entry" />
                    </Field>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Current Value">
                      <NumInput value={d.current_value} onChange={v => setField(inv.id, 'current_value', v)} />
                    </Field>
                    <Field label="Ownership %">
                      <NumInput value={d.ownership_percentage} onChange={v => setField(inv.id, 'ownership_percentage', v)} placeholder="e.g. 2.5" />
                    </Field>
                  </div>

                  <Field label="Notes">
                    <textarea
                      value={d.notes ?? ''}
                      onChange={e => setField(inv.id, 'notes', e.target.value || null)}
                      rows={2}
                      className="w-full px-3 py-2 text-sm border border-[#e5e5e5] focus:border-[#1a1a1a] outline-none bg-white text-[#1a1a1a] resize-none"
                    />
                  </Field>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        {!loadingInv && investments.length > 0 && (
          <div className="px-6 py-4 border-t border-[#e5e5e5] shrink-0 flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-[#e5e5e5] text-[#4a4a4a] hover:border-[#1a1a1a] hover:text-[#1a1a1a]">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm bg-[#ffd87a] border border-[#1a1a1a] text-[#1a1a1a] font-medium flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function StartupsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStage, setSelectedStage] = useState(null);
  const [editCompany, setEditCompany] = useState(null);

  useEffect(() => { fetchStartups(); }, []);

  const fetchStartups = async () => {
    try {
      const res = await axios.get(`${API}/startups/companies`);
      setStartups(res.data || []);
    } catch {
      toast.error('Failed to load portfolio companies');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => {
    if (!v && v !== 0) return '—';
    return fmtCur(v, user?.market);
  };

  const fmtPct = (v) => v != null ? `${parseFloat(v).toFixed(2)}%` : '—';

  const moic = (invested, current) => {
    if (!invested || !current) return null;
    return (current / invested).toFixed(2);
  };

  const filtered = startups.filter(s => {
    const q = searchTerm.toLowerCase();
    const matchSearch = s.name?.toLowerCase().includes(q) || s.sector?.toLowerCase().includes(q);
    const matchStage = !selectedStage || s.stage === selectedStage;
    return matchSearch && matchStage;
  });

  const totalInvested = startups.reduce((sum, s) => sum + (s.total_invested || 0), 0);
  const totalValue    = startups.reduce((sum, s) => sum + (s.current_value || 0), 0);
  const totalGain     = totalValue - totalInvested;
  const portfolioMoic = totalInvested > 0 ? (totalValue / totalInvested).toFixed(2) : '—';
  const activeCount   = startups.filter(s => s.status === 'active').length;
  const preIpoCount   = startups.filter(s => s.stage === 'pre_ipo' || s.status === 'ipo').length;

  const presentStages = [...new Set(startups.map(s => s.stage).filter(Boolean))];

  return (
    <div className="p-6 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#1a1a1a] flex items-center justify-center shrink-0">
            <Rocket className="w-5 h-5 text-[#ffd87a]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#1a1a1a]">Portfolio Companies</h1>
            <p className="text-sm text-[#4a4a4a]">Startup investments &amp; cap table tracker</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#2a2a2a] border border-[#2a2a2a] mb-6">
        <StatCard label="Companies"      value={startups.length}   sub={`${activeCount} active`} />
        <StatCard label="Invested"       value={fmt(totalInvested)} />
        <StatCard
          label="Unrealised Gain"
          value={fmt(Math.abs(totalGain))}
          sub={totalGain >= 0 ? '▲ vs cost' : '▼ vs cost'}
        />
        <StatCard label="Portfolio MOIC" value={`${portfolioMoic}×`} sub="blended" />
        <StatCard label="Pre-IPO / Listed" value={preIpoCount} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a4a4a]" />
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search company or sector…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-[#e5e5e5] outline-none focus:border-[#ffd87a] bg-white text-[#1a1a1a]"
          />
        </div>
        {presentStages.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setSelectedStage(null)}
              className={`px-3 py-1 text-xs font-medium border transition-colors ${
                !selectedStage ? 'bg-[#1a1a1a] text-white border-[#1a1a1a]' : 'bg-white text-[#4a4a4a] border-[#e5e5e5] hover:border-[#1a1a1a] hover:text-[#1a1a1a]'
              }`}
            >All</button>
            {presentStages.map(s => (
              <button
                key={s}
                onClick={() => setSelectedStage(selectedStage === s ? null : s)}
                className={`px-3 py-1 text-xs font-medium border transition-colors ${
                  selectedStage === s ? 'bg-[#1a1a1a] text-white border-[#1a1a1a]' : 'bg-white text-[#4a4a4a] border-[#e5e5e5] hover:border-[#1a1a1a] hover:text-[#1a1a1a]'
                }`}
              >
                {STAGE_CONFIG[s]?.label || s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="border border-[#e5e5e5] overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-6 h-6 text-[#ffd87a] animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Building2 className="w-10 h-10 text-[#4a4a4a] mx-auto mb-3" />
            <p className="text-sm text-[#4a4a4a]">No portfolio companies found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-[#dae4c4]">
                <th className="text-left px-5 py-3 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Company</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Sector</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Stage</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Invested</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Entry Valuation</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Ownership</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Current Value</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">MOIC</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Status</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e5e5e5]">
              {filtered.map((company, idx) => {
                const stageConf  = STAGE_CONFIG[company.stage]  || { label: company.stage };
                const statusConf = STATUS_CONFIG[company.status] || { label: company.status, dot: 'bg-[#a8a8a8]' };
                const m    = moic(company.total_invested, company.current_value);
                const gain = (company.current_value || 0) - (company.total_invested || 0);

                return (
                  <tr
                    key={company.id}
                    onClick={() => navigate(`/startups/${company.id}`)}
                    className={`transition-colors cursor-pointer hover:bg-[#fffbef] ${idx % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}`}
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#1a1a1a] flex items-center justify-center shrink-0">
                          <span className="text-[10px] font-bold text-[#ffd87a]">{company.name?.slice(0, 2).toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="font-medium text-[#1a1a1a] text-sm">{company.name}</p>
                          <p className="text-xs text-[#4a4a4a]">{company.headquarters || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-[#4a4a4a] max-w-[140px]">
                      <span className="truncate block">{company.sector || '—'}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="px-2 py-0.5 text-xs font-medium border border-[#e5e5e5] text-[#1a1a1a] bg-white">
                        {stageConf.label}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right text-sm text-[#1a1a1a]">
                      {fmt(company.total_invested)}
                    </td>
                    <td className="px-4 py-3.5 text-right text-sm text-[#4a4a4a]">
                      {fmt(company.latest_valuation)}
                    </td>
                    <td className="px-4 py-3.5 text-right text-sm text-[#4a4a4a]">
                      {fmtPct(company.ownership_percentage)}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <p className="text-sm font-medium text-[#1a1a1a]">{fmt(company.current_value)}</p>
                      {gain !== 0 && (
                        <p className={`text-xs flex items-center justify-end gap-0.5 ${gain >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                          {gain >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                          {fmt(Math.abs(gain))}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      {m ? (
                        <span className={`font-bold text-sm ${parseFloat(m) >= 1 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                          {m}×
                        </span>
                      ) : (
                        <span className="text-[#4a4a4a]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <div className="inline-flex items-center gap-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${statusConf.dot}`} />
                        <span className="text-xs text-[#4a4a4a]">{statusConf.label}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3.5 text-center">
                      <button
                        onClick={e => { e.stopPropagation(); setEditCompany(company); }}
                        title="Quick edit investments"
                        className="text-[#4a4a4a] hover:text-[#1a1a1a]"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-[#4a4a4a] mt-3">Click any row to view company details. Use the edit icon for a quick investment update.</p>

      {editCompany && (
        <EditDrawer
          company={editCompany}
          fmt={fmt}
          onClose={() => setEditCompany(null)}
          onSaved={fetchStartups}
        />
      )}
    </div>
  );
}
